import React from 'react';
import { useNavigate } from 'react-router-dom';
import {
  useGetUserMatchesQuery,
  UserMatch,
  FEEDBACK_LABELS,
  SUGGESTION_TYPE_LABELS,
  Feedback,
  SuggestionType,
} from 'src/store/matchTestApi';
import { Button } from 'src/ui/Button';
import { Badge } from 'src/ui/Badge';
import { Spinner } from 'src/ui/Spinner';
import { Alert } from 'src/ui/Alert';

interface MatchesListProps {
  userId: string;
  selectedMatchId?: number;
  onMatchSelect: (match: UserMatch | null) => void;
  showBackButton?: boolean;
}

const MatchesList = ({
  userId,
  selectedMatchId,
  onMatchSelect,
  showBackButton = true,
}: MatchesListProps) => {
  const navigate = useNavigate();
  const {
    data: matchesData,
    isLoading,
    error,
  } = useGetUserMatchesQuery(userId);

  const handleBackToProfile = () => {
    navigate(`/user-profile/${userId}`);
  };

  const getStatusColor = (status: any): string => {
    if (!status || typeof status !== 'string') return 'gray';
    switch (status.toLowerCase()) {
      case 'matched':
        return 'green';
      case 'pending':
        return 'amber';
      case 'rejected':
        return 'red';
      default:
        return 'gray';
    }
  };

  const formatScore = (score: number) => {
    return (score * 100).toFixed(1) + '%';
  };

  const Header = () => (
    <div
      style={{
        padding: 16,
        borderBottom: '1px solid var(--border)',
        flexShrink: 0,
      }}
    >
      {showBackButton && (
        <div style={{ marginBottom: 8 }}>
          <Button
            variant="ghost"
            size="sm"
            leftIcon="arrow-left"
            onClick={handleBackToProfile}
          >
            Back to Profile
          </Button>
        </div>
      )}
      <div
        style={{
          fontWeight: 600,
          fontSize: 'var(--fs-md)',
          color: 'var(--text)',
        }}
      >
        User Matches
      </div>
    </div>
  );

  if (isLoading) {
    return (
      <div style={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
        <Header />
        <div
          style={{
            flex: 1,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          <Spinner size="lg" />
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div style={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
        <Header />
        <div style={{ padding: 16 }}>
          <Alert color="red">Failed to load matches</Alert>
        </div>
      </div>
    );
  }

  return (
    <div style={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
      {/* Fixed Header */}
      <div
        style={{
          padding: 16,
          borderBottom: '1px solid var(--border)',
          flexShrink: 0,
        }}
      >
        {showBackButton && (
          <div style={{ marginBottom: 8 }}>
            <Button
              variant="ghost"
              size="sm"
              leftIcon="arrow-left"
              onClick={handleBackToProfile}
            >
              Back to Profile
            </Button>
          </div>
        )}
        <div
          style={{
            fontWeight: 600,
            fontSize: 'var(--fs-md)',
            color: 'var(--text)',
          }}
        >
          User Matches
        </div>
        <div
          style={{
            fontSize: 'var(--fs-sm)',
            color: 'var(--text-muted)',
            marginTop: 2,
          }}
        >
          Total: {matchesData?.totalMatches || 0} matches
        </div>
      </div>

      {/* Scrollable Content */}
      <div style={{ flex: 1, overflowY: 'auto' }}>
        {!matchesData?.matches || matchesData.matches.length === 0 ? (
          <div
            style={{
              height: '100%',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <span
              style={{ color: 'var(--text-muted)', fontSize: 'var(--fs-sm)' }}
            >
              No matches found for this user
            </span>
          </div>
        ) : (
          <div>
            {matchesData.matches.map((match) => (
              <div
                key={match.matchId}
                style={{
                  padding: 16,
                  borderBottom: '1px solid var(--border-subtle)',
                  cursor: 'pointer',
                  background:
                    selectedMatchId === match.matchId
                      ? 'var(--bg-selected)'
                      : undefined,
                }}
                onClick={() => onMatchSelect(match)}
              >
                <div className="col" style={{ gap: 6 }}>
                  <div
                    className="row"
                    style={{
                      justifyContent: 'space-between',
                      alignItems: 'center',
                    }}
                  >
                    <span
                      style={{
                        fontWeight: 500,
                        fontSize: 'var(--fs-sm)',
                        color: 'var(--text)',
                      }}
                    >
                      {match.matchedUser.name || match.matchedUser.email}
                    </span>
                    <Badge
                      color={getStatusColor(match.status) as any}
                      size="sm"
                    >
                      {match.status || 'Unknown'}
                    </Badge>
                  </div>

                  <span
                    style={{
                      fontSize: 'var(--fs-xs)',
                      color: 'var(--text-secondary)',
                    }}
                  >
                    {match.matchedUser.email}
                  </span>

                  <div
                    className="row"
                    style={{ justifyContent: 'space-between' }}
                  >
                    <span
                      style={{
                        fontSize: 'var(--fs-xs)',
                        color: 'var(--text-muted)',
                      }}
                    >
                      User Score: {formatScore(match.scores.userScore)}
                    </span>
                    <span
                      style={{
                        fontSize: 'var(--fs-xs)',
                        color: 'var(--text-muted)',
                      }}
                    >
                      Match Score: {formatScore(match.scores.matchedUserScore)}
                    </span>
                  </div>

                  {match.suggestion && (
                    <div
                      className="row"
                      style={{
                        justifyContent: 'space-between',
                        alignItems: 'center',
                      }}
                    >
                      <Badge
                        size="sm"
                        color={
                          match.suggestion.type === SuggestionType.MATCH
                            ? 'blue'
                            : 'purple'
                        }
                      >
                        {SUGGESTION_TYPE_LABELS[
                          match.suggestion.type as SuggestionType
                        ] || 'Unknown'}
                      </Badge>
                      {match.suggestion.feedback !== null && (
                        <Badge
                          size="sm"
                          color={
                            match.suggestion.feedback === Feedback.LIKE
                              ? 'green'
                              : 'red'
                          }
                        >
                          {
                            FEEDBACK_LABELS[
                              match.suggestion.feedback as Feedback
                            ]
                          }
                        </Badge>
                      )}
                    </div>
                  )}

                  <span
                    style={{
                      fontSize: 'var(--fs-xs)',
                      color: 'var(--text-faint)',
                    }}
                  >
                    {new Date(match.createdAt).toLocaleDateString()}
                  </span>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default MatchesList;
